import { KeyboardEvent, CSSProperties, useState } from 'react'
import { Formik, Form, FieldProps, Field } from 'formik'
import cx from 'classnames'
import { HandbookItem, SectionHandbookItem } from 'types'

import Styles from './Section.module.scss'
import HandbookItemsStyles from '../HandbookItems.module.scss'
import { useHandbookItems } from '../HandbookItemProvider'
import { HandbookItemParams } from 'api/clients/HandbookClient'
import { useBlurOnNavigateRef } from './hooks'
import { observer } from 'mobx-react-lite'
import { useRootStore } from 'stores/RootStore'
import { ChevronRight } from 'almanac-ui/icons'
import { isLegacyHandbook } from 'components/Handbook2_0/util'
import { SectionItemMenu } from 'components/Handbook2_0/ContextMenus/ItemMenu/SectionItemMenu'
import { useHandbookPermissions } from 'components/Handbook2_0/hooks'
import { MenuType } from 'components/Handbook2_0/ContextMenus/Types'

interface Props {
  item: SectionHandbookItem
  depth?: number
}

export const Section = observer((props: Props) => {
  const { item, depth } = props
  const treeItem = useRootStore().handbooks.items.get(item.id.toString())
  const handbook = useRootStore().handbooks.get(treeItem?.data.handbook.key)
  const isLegacy = handbook && isLegacyHandbook(handbook.handbook)
  const { canEdit } = useHandbookPermissions(handbook?.handbook)
  const [isRenaming, setIsRenaming] = useState(false)

  const toggle = () => {
    if (!isLegacy) {
      return
    }
    treeItem?.isExpanded ? treeItem.collapse() : treeItem?.expand()
  }

  if (isRenaming) {
    return (
      <div className={HandbookItemsStyles.renameWrapper}>
        <SectionForm item={item} onCancel={() => setIsRenaming(false)} onRename={() => setIsRenaming(false)} />
      </div>
    )
  }

  return (
    <SectionItemMenu
      item={item}
      currentUserCanAccess={canEdit}
      type={MenuType.contextMenu}
      onEdit={() => setIsRenaming(true)}
    >
      <div
        className={cx(Styles.container, { [Styles.legacy]: isLegacy })}
        style={
          {
            '--indent-level': depth,
          } as CSSProperties
        }
        onClick={toggle}
      >
        {isLegacy && (
          <button className={cx(Styles.expandButton, { [Styles.expanded]: treeItem?.isExpanded })}>
            <ChevronRight />
          </button>
        )}
        <span className={Styles.title}>{item.title}</span>
      </div>
    </SectionItemMenu>
  )
})

interface SectionFormProps {
  item: HandbookItem
  onCancel?: () => void
  onRename?: () => void
}

function SectionForm({ item, onCancel, onRename }: SectionFormProps) {
  const inputEl = useBlurOnNavigateRef<HTMLInputElement>()
  const { currentHandbook: handbook, updateItem } = useHandbookItems()

  if (!handbook) return null

  return (
    <Formik<HandbookItemParams>
      onSubmit={async (values) => {
        await updateItem(item, values)
        onRename?.()
      }}
      initialValues={{ title: item.title }}
      validate={(values) => {
        const errors = {} as { title?: boolean }
        if (!values.title) {
          errors.title = true
        }
        return errors
      }}
    >
      {({ isValid, submitForm }) => (
        <Form>
          <div
            className={cx(HandbookItemsStyles.inputWrapper, Styles.section, { [HandbookItemsStyles.error]: !isValid })}
          >
            <Field name="title">
              {({ field }: FieldProps) => (
                <input
                  ref={inputEl}
                  {...field}
                  autoFocus
                  placeholder="Section name"
                  onKeyDown={(e: KeyboardEvent) => {
                    if (e.key === 'Escape') {
                      onCancel?.()
                    }
                  }}
                  onBlur={(e) => {
                    field.onBlur(e)
                    if (field.value && isValid) {
                      return submitForm()
                    }
                    onCancel?.()
                  }}
                />
              )}
            </Field>
          </div>
        </Form>
      )}
    </Formik>
  )
}
